import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import {
  fetchStyles,
  setStyleFilters,
  clearError
} from '../store/slices/inventorySlice';
import { 
  RefreshCw,
  AlertTriangle,
  Package,
  Upload
} from 'lucide-react';
import StyleManagement from '../components/inventory/StyleManagement';
import StyleUpload from '../components/inventory/StyleUpload';

const StyleManagementPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { 
    styles,
    stylesLoading,
    stylesError,
    stylesPagination,
    styleFilters
  } = useAppSelector((state) => state.inventory);
  const { user } = useAppSelector((state) => state.auth);

  // Local state for UI
  const [activeTab, setActiveTab] = useState<'styles' | 'upload'>('styles');

  useEffect(() => {
    // Load styles for managers only
    if (user?.role === 'manager') {
      dispatch(fetchStyles(styleFilters));
    }
  }, [dispatch, user?.role, styleFilters]);

  useEffect(() => {
    if (stylesError) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [stylesError, dispatch]);

  const handleRefresh = () => {
    dispatch(clearError());
    if (user?.role === 'manager') {
      dispatch(fetchStyles(styleFilters));
    }
  };
  const handlePageChange = (page: number) => {
    const filters = { ...styleFilters, page };
    dispatch(setStyleFilters(filters));
    dispatch(fetchStyles(filters));
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'styles':
        return (
          <StyleManagement 
            styles={styles}
            loading={stylesLoading}
            pagination={stylesPagination}
            onPageChange={handlePageChange}
          />
        );
      
      case 'upload':
        return <StyleUpload />;
      
      default:
        return null;
    }
  };

  // Only managers can access this page
  if (user?.role !== 'manager') {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <Package className="h-24 w-24 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Style Management</h1>
          <p className="text-gray-600">You don't have permission to access style management.</p>
          <p className="text-sm text-gray-500 mt-2">This feature is available to Store Managers only.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Style Management</h1>
          <p className="text-gray-600">Create and manage product styles, variants, and images</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={stylesLoading}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${stylesLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Error Display */}
      {stylesError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{stylesError}</p>
            </div>
          </div>
        </div>
      )}      {/* Style Management Features */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertTriangle className="h-5 w-5 text-blue-500 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Style Management Features</h3>
            <div className="text-sm text-blue-700 mt-1 space-y-1">
              <p>• Create new styles with multiple images and variants</p>
              <p>• Edit existing style information and properties</p>
              <p>• Manage sizes, colors, and SKU codes</p>
              <p>• Upload style catalog from Shopify exports</p>
              <p>• Filter and search through style catalog</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('styles')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'styles'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Package className="h-4 w-4 inline mr-2" />
            Style Catalog
          </button>

          <button
            onClick={() => setActiveTab('upload')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'upload'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Upload className="h-4 w-4 inline mr-2" />
            Upload Styles
          </button>
        </nav>
      </div>

      {/* Main Style Management Component */}
      <div className="bg-white rounded-lg shadow">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default StyleManagementPage;
